import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, {
  IActionData,
  ITableColumn,
} from '@/components/common/CommonTable';
import MainHeading from '@/components/common/MainHeading';
import EditIcon from '@/components/Icons/EditIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import { RootState } from '@/redux/store';
import {
  assetStatus,
  getFilterUrl,
  getToken,
  orderByList,
  showToastNotification,
} from '@/utility/helper';
import moment from 'moment';
import { RoutesLink } from '@/utility/RouteLinks';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IAssetCategoryResponse,
  IAssetResponse,
} from '@/interfaces/assets.model';
import FilterComponent from '@/components/common/FilterComponent';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { useDispatch } from 'react-redux';
import {
  IDeleteResponse,
  IFilterSideBarForm,
  ITableRowData,
} from '@/interfaces/common.model';
import useFilter from '@/hooks/useFilter';
import PlusIcon from '@/components/Icons/PlusIcon';
import AssetTransferIcon from '@/components/Icons/AssetTransferIcon';
import ReturnAssetIcon from '@/components/Icons/ReturnAssetIcon';
import AssignAssetModal from '@/components/Modal/AssignAssetModal';
import ActionModal from '@/components/Modal/ActionModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import DeleteIcon from '@/components/Icons/DeleteIcon';
export interface ITableData extends ITableRowData {
  index: number;
  name: string;
  description: string;
  id: string;
  serialNumber: string;
  price: string;
  assetId?: string | null;
  createdAt?: string | null;
  assetStatus: number;
  assetCategory: string;
  assetRequest: string;
}

interface IPageState {
  currentPage: number;
  count: number;
  totalPages: number;
  tableLoader: boolean;
  assets: ITableData[];
  filterData: string;
  searchQuery: string;
  assetCategory: { id: string; name: string }[];
}

const Assets = () => {
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [pageStates, setPageStates] = useState<IPageState>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    assets: [],
    filterData: '',
    searchQuery: '',
    assetCategory: [],
  });

  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    { value: 'name', label: 'Name' },
    { value: 'serialNumber', label: 'Serial No.' },
    { value: 'price', label: 'Price' },
    { value: 'assetRequest', label: 'Assigned To' },
    { value: 'assetStatus', label: 'Status' },
    // { value: 'description', label: 'Description' },
  ];

  const [isAssignModalOpen, setIsAssignModalOpen] = useState<boolean>(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [isAssignLoading, setIsAssignLoading] = useState<boolean>(false);

  const [isReturnModalOpen, setIsReturnModalOpen] = useState<boolean>(false);
  const [isReturnLoading, setIsReturnLoading] = useState<boolean>(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteAssetId, setDeleteAssetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  const [isExporting, setIsExporting] = useState<boolean>(false);

  const ActionData: IActionData<ITableData>[] = [
    {
      label: 'View',
      className: 'btn btn-link ',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: (data: ITableData) =>
        navigate(RoutesLink?.assetDetail + `/${data?.id}`),
    },
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: (data: ITableData) =>
        navigate(RoutesLink?.addAsset + `/${data?.id}`),
    },
    {
      label: 'Assign',
      className: 'btn btn-link',
      icon: <AssetTransferIcon />,
      title: 'Assign',
      clickHandler: (data: ITableData) => {
        setSelectedAssetId(data?.id);
        setIsAssignModalOpen(true);
      },
      isHidden: (data: ITableData) => Number(data?.assetStatus) !== 1,
    },
    {
      label: 'Return',
      className: 'btn btn-link text-danger',
      icon: <ReturnAssetIcon />,
      title: 'Return',
      clickHandler: (data: ITableData) => {
        setSelectedAssetId(data?.id);
        setIsReturnModalOpen(true);
      },
      isHidden: (data: ITableData) => Number(data?.assetStatus) !== 0,
    },
    {
      label: 'Delete',
      className: 'btn btn-link text-danger',
      icon: <DeleteIcon />,
      title: 'Delete',
      clickHandler: (data: ITableData) => {
        setDeleteAssetId(data?.id);
        setIsDeleteModalOpen(true);
      },
    },
  ];

  const getCategories = useCallback(async () => {
    try {
      const response: IAssetCategoryResponse = await callApi(
        `${API_URL}${API_ENDPOINT?.getAssetsCategory}`,
        {
          method: 'GET',
        },
      );
      if (response.status) {
        setPageStates((prev) => ({
          ...prev,
          assetCategory: response.data?.assetCategory,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const getAssets = useCallback(async () => {
    try {
      setPageStates((prev) => ({
        ...prev,
        tableLoader: true,
      }));
      const filter = {
        search: pageStates.searchQuery,
        pageSize: filterDetails?.pageSize,
        pageNumber: pageStates.currentPage,
        assetCategoryId: id || filterDetails?.assetCategoryId || '',
        status: filterDetails?.assetStatus || '',
        orderBy: filterDetails?.orderBy || '',
      };
      const API = `${API_URL}${API_ENDPOINT?.assetList}?${getFilterUrl(filter)}`;
      const response: IAssetResponse = await callApi(API, {
        method: 'GET',
      });
      if (response.status) {
        const newData: ITableData[] = response?.data?.asset?.map(
          (item, index: number) => ({
            ...item,
            index: index + 1,
            name: item?.name,
            serialNumber: item?.serialNo,
            price: item?.price,
            assetCategory: item?.assetCategory?.name ?? '-',
            assetStatus: Number(item?.status),
            description: item.description,
            id: item.id,
            assetRequest: item?.assetRequest?.assignedUser?.name ?? '-',
          }),
        );
        setPageStates((prev) => ({
          ...prev,
          assets: newData,
          tableLoader: false,
          totalPages: response?.data?.pageInfo?.totalPages,
          count: response?.data?.pageInfo?.total ?? 0,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({
        ...prev,
        tableLoader: false,
      }));
    }
  }, [
    id,
    pageStates.searchQuery,
    pageStates.currentPage,
    filterDetails?.pageSize,
    filterDetails?.assetCategoryId,
    filterDetails?.assetStatus,
    filterDetails?.orderBy,
  ]);

  useEffect(() => {
    getAssets();
  }, [getAssets]);

  const handleStatusToggle = async (data: ITableData, status: boolean) => {
    const updatedAssets = pageStates?.assets.map((asset) => {
      if (asset?.id == data?.id) {
        return { ...asset, toggleStatus: !status };
      }
      return asset;
    });
    setPageStates((prev) => ({ ...prev, assets: updatedAssets }));
  };
  const sortHandler = (data: Partial<IFilterSideBarForm> | null) => {
    if (data === null) {
      dispatch(
        setUpdateFilterStates({
          assetCategoryId: '',
          assetStatus: '',
        }),
      );
      return;
    }

    if (data) {
      dispatch(
        setUpdateFilterStates({
          assetCategoryId: data.assetCategoryId,
          assetStatus: data?.assetStatus,
        }),
      );
    }
    setPageStates((prev) => ({ ...prev, currentPage: 1 }));
  };

  const {
    handleSearchChange,
    orderByHandler,
    pageChangeHandler,
    handleChangePageSize,
  } = useFilter(pageStates, setPageStates);

  const handleAssignSubmit = async (assignedId: string) => {
    if (!selectedAssetId) return;
    try {
      setIsAssignLoading(true);
      const API = `${API_URL}${API_ENDPOINT?.updateAssetRequest}`;
      const response: IDeleteResponse = await callApi(API, {
        method: 'POST',
        data: {
          assetId: selectedAssetId,
          assignedId,
          // isTransfer: true,
          isAllocate: true,
          status: 1,
        },
      });
      if (response?.status) {
        showToastNotification(
          'success',
          response?.message || 'Asset assigned successfully',
        );
        setIsAssignModalOpen(false);
        setSelectedAssetId(null);
        getAssets();
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsAssignLoading(false);
    }
  };

  const handleReturnSubmit = async () => {
    if (!selectedAssetId) return;
    try {
      setIsReturnLoading(true);
      const API = `${API_URL}${API_ENDPOINT?.updateAssetRequest}`;
      const response: IDeleteResponse = await callApi(API, {
        method: 'POST',
        data: {
          assetId: selectedAssetId,
          status: 1,
          isReturn: true,
        },
      });
      if (response?.status) {
        showToastNotification(
          'success',
          response?.message || 'Asset returned successfully',
        );
        setIsReturnModalOpen(false);
        setSelectedAssetId(null);
        getAssets();
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsReturnLoading(false);
    }
  };

  const handleDeleteAsset = async () => {
    if (!deleteAssetId) return;
    try {
      setIsDeleteLoading(true);
      const API = `${API_URL}${API_ENDPOINT?.deleteAssets}${deleteAssetId}`;
      const response: IDeleteResponse = await callApi(API, {
        method: 'GET',
      });
      if (response?.status) {
        showToastNotification(
          'success',
          response?.message || 'Asset deleted successfully',
        );
        setIsDeleteModalOpen(false);
        setDeleteAssetId(null);
        getAssets();
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to delete asset',
        );
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleDownloadAssignedAssetExcel = async () => {
    try {
      setIsExporting(true);
      // showToastNotification('info', 'Downloading assigned assets report...');
      const API = `${API_URL}${API_ENDPOINT?.downloadAssignedAssetExcel}`;
      const response = await fetch(API, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (!response.status) {
        throw new Error('Failed to download assigned asset report');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const json = await response.json();
        showToastNotification(
          'danger',
          json?.message || 'Failed to download report',
        );
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('content-disposition');
      let fileName = `Assigned_Assets_Report_${moment().format('YYYY-MM-DD')}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/i);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToastNotification('success', 'Report downloaded successfully');
    } catch (error: unknown) {
      console.error('Download error:', error);
      showToastNotification(
        'danger',
        'Failed to download assigned asset report',
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'Assets'}
          buttonText={'Add Asset'}
          buttonIcon={<PlusIcon />}
          clickHandler={() => navigate(RoutesLink?.addAsset)}
        />
        <CardLayout cardStyle="mb-4">
          <FilterComponent
            isGrid={false}
            handleSearch={handleSearchChange}
            filterArray={{ isShow: true }}
            orderByList={orderByList}
            orderByHandler={orderByHandler}
            sortHandler={sortHandler}
            handleExport={handleDownloadAssignedAssetExcel}
            exportLoading={isExporting}
            exportButtonText="Export"
            assetSortList={{
              assetCategory: pageStates?.assetCategory,
              assetStatus: assetStatus,
            }}
          />

          <CommonTable<ITableData>
            tableHeading={tableHeading}
            tableData={pageStates?.assets}
            ActionData={ActionData}
            count={pageStates?.count}
            pageSize={filterDetails?.pageSize}
            isLoading={pageStates?.tableLoader}
            currentPage={pageStates?.currentPage}
            totalPages={pageStates?.totalPages}
            handleStatusToggle={handleStatusToggle}
            pageChangeHandler={pageChangeHandler}
            handleChangePageSize={handleChangePageSize}
          />
        </CardLayout>
      </div>

      <AssignAssetModal
        isOpen={isAssignModalOpen}
        toggle={() => {
          setIsAssignModalOpen(false);
          setSelectedAssetId(null);
        }}
        submitHandler={handleAssignSubmit}
        isLoading={isAssignLoading}
      />

      <ActionModal
        isOpen={isReturnModalOpen}
        toggle={() => {
          setIsReturnModalOpen(false);
          setSelectedAssetId(null);
        }}
        submitHandler={handleReturnSubmit}
        isLoading={isReturnLoading}
        title="Return Asset"
        message="Are you sure you want to return this asset?"
        btnText="Return"
        btnClass="btn-danger"
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        toggle={() => {
          setIsDeleteModalOpen(false);
          setDeleteAssetId(null);
        }}
        submitHandler={handleDeleteAsset}
        isLoading={isDeleteLoading}
      />
    </Layout>
  );
};
export default Assets;
